import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { MapPin, ShieldCheck, Zap, Gauge, ArrowRight, Star, ChevronLeft, ChevronRight, Clock } from "lucide-react";

const images = [
  "assets/about-2.webp",
  "assets/about-3.webp",
  "assets/about-4.webp",
  "assets/about-5.webp",
  "assets/about-6.webp",
  "assets/about-7.webp",
  "assets/about-8.webp",
  "assets/about-9.webp",
  "assets/about-10.webp",
];



const AboutUs = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  const stats = [
    { num: 100000, label: "Happy Customers" },
    { num: 500, label: "Fleet Size" },
    { num: 4.9, label: "Average Rating" },
    { num: 5000, label: "Google Reviews" }
  ];

  const [counts, setCounts] = useState(stats.map(() => 0));
  const [currentSlide, setCurrentSlide] = useState(0);
  const shorts = [
    "f54UOAMYx1s", "oPFO_B58EcY", "pJkorXpn55w", "rf0b_0FGsCI", "Ei1GDqqMsAo", "XD5RS0U6Vw0"
  ];
  const [current, setCurrent] = useState(0);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % shorts.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + shorts.length) % shorts.length);

  const features = [
    { icon: <MapPin size={24} />, title: "Doorstep Delivery", desc: "We bring your chosen ride right to your home or office." },
    { icon: <Clock size={24} />, title: "24/7 Support", desc: "Round-the-clock assistance whenever you need us." },
    { icon: <Zap size={24} />, title: "Self-Drive Freedom", desc: "No chauffeurs. Your car, your music, your road." },
    { icon: <ShieldCheck size={24} />, title: "Transparent Pricing", desc: "No hidden charges. Fast refunds, guaranteed." },
    { icon: <Gauge size={24} />, title: "Rigorous Safety", desc: "Professional deep-clean and safety checks." },
  ];

  useEffect(() => {
    const duration = 5000;
    const steps = 100;
    const intervalTime = duration / steps;

    const intervals = stats.map((stat, index) => {
      let step = 0;
      const increment = stat.num / steps;

      return setInterval(() => {
        step++;
        setCounts((prev) => {
          const updated = [...prev];
          updated[index] = Math.min(increment * step, stat.num);
          return updated;
        });

        if (step >= steps) clearInterval(intervals[index]);
      }, intervalTime);
    });

    return () => intervals.forEach(clearInterval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div ref={containerRef} className="page-wrapper">
      <style>{css}</style>
      
      <motion.div className="progress-bar" style={{ scaleX }} />

      {/* --- HERO SECTION --- */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="hero-badge">
            <div className="pulse-dot" /> Premium Rentals Bangalore
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8 }} 
            className="hero-title"
          >
            DRIVE THE <br /> <span className="text-stroke">UNEXPECTED</span>
          </motion.h1>
          <p className="hero-sub">Luxury, comfort, and the freedom to explore. Wow Carz provides the keys to your next great story.</p>
          {/* <div className="hero-btn-group">
             <button className="btn-primary">View Fleet <ArrowRight size={18} /></button>
             <button className="btn-outline">Our Story</button>
          </div> */}
        </div>
      </section>

      <div className="stats-band">
        {stats.map((s, i) => (
          <div key={i} className="stat-item">
            <span className="stat-num">
              {(s.num < 10 ? counts[i].toFixed(1) : Math.floor(counts[i]).toLocaleString()) + "+"}
            </span>
            <span className="stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* --- VIDEO SLIDER SECTION --- */}
      <section className="section bg-dark video-section">
        <div className="container grid-video">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="video-text">
            <span className="overline" style={{ color: "var(--red)" }}>Customer Stories</span>
            <h2 className="section-title text-white">SEE THE <br />EXPERIENCE</h2>
            <p className="body-text" style={{ color: "rgba(255,255,255,0.6)" }}>
              Watch why thousands of travelers choose Wow Carz. Real reviews, real people, real adventures.
            </p>
            
            <div className="slider-controls">
              <button onClick={prevSlide} className="nav-btn" aria-label="Previous slide"><ChevronLeft size={24} /></button>
              <div className="slide-counter">
                <span style={{ color: "var(--red)" }}>0{currentSlide + 1}</span> / 0{shorts.length}
              </div>
              <button onClick={nextSlide} className="nav-btn" aria-label="Next slide"><ChevronRight size={24} /></button>
            </div>
          </motion.div>

          <div className="youtube-slider-container">
            <div className="video-glow" />
            <div className="phone-frame">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  transition={{ duration: 0.4 }}
                  className="iframe-wrapper"
                >
                  <iframe 
                    src={`https://www.youtube.com/embed/${shorts[currentSlide]}?modestbranding=1&rel=0`} 
                    title="WowCarz Review"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* --- STORY SECTION --- */}
      <section className="section section-story">
        <div className="container grid-story">
          <div className="story-content">
            <span className="overline">Our Mission</span>
            <h2 className="section-title">REDEFINING <br/>MOBILITY</h2>
            <p className="body-text">Founded in Bangalore, <strong>Wow Carz</strong> was born to fix car rentals. We built a system based on radical transparency and convenience.</p>
            <div className="trust-box">
              <div className="avatar-stack">
                {[11,12,13].map(n => <img key={n} src={`https://i.pravatar.cc/100?img=${n}`} alt="user" />)}
              </div>
              <div>
                <p style={{ fontWeight: 700, margin: 0 }}>4.9/5 Rating</p>
                <p style={{ opacity: 0.6, fontSize: "0.75rem", margin: 0 }}>10,000+ Reviews</p>
              </div>
            </div>
          </div>
          <div className="story-image-wrapper">
            <div className="relative w-full h-[400px] overflow-hidden rounded-xl">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt="Premium Car"
                  className={`absolute w-full h-full object-cover transition-opacity duration-700 ${
                    index === current ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
            </div>
            <div className="floating-badge"><Star fill="#fff" size={14} /> <span>#1 in Bangalore</span></div>
          </div>
        </div>
      </section>

      {/* --- FEATURES SECTION --- */}
      <section className="section bg-dark">
        <div className="container">
          <span className="overline" style={{ color: "rgba(255,255,255,0.4)" }}>Why Choose Us</span>
          <h2 className="section-title text-white">THE WOW ADVANTAGE</h2>
          <div className="grid-features">
            {features.map((f, i) => (
              <div key={i} className="feature-card">
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@300;400;600;800&display=swap');

  :root {
    --red: #D0281A;
    --black: #0D0D0D;
    --cream: #F4F1ED;
    --transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', sans-serif; background: var(--cream); overflow-x: hidden; color: var(--black); }

  .container { width: 90%; max-width: 1200px; margin: 0 auto; }
  .progress-bar { position: fixed; top: 0; left: 0; right: 0; height: 4px; background: var(--red); z-index: 1000; transform-origin: 0%; }

  /* Hero Base */
  .hero { 
    min-height: 100vh; 
    position: relative; 
    display: flex; 
    align-items: center; 
    justify-content: center; /* Mobile: Centered */
    padding: clamp(2rem, 10vh, 8rem) 5%; 
    overflow: hidden; 
    background: #000; 
  }
  .hero-bg { 
    position: absolute; 
    inset: 0; 
    background: url('assets/bg-about.webp') center/cover no-repeat; 
    opacity: 0.4; 
  }
  
  /* Hero Overlay - Switched to Right-biased for Desktop readability */
  .hero-overlay { 
    position: absolute; 
    inset: 0; 
    background: linear-gradient(to right, rgba(0,0,0,0) 0%, transparent 60%, rgba(0,0,0,0.8) 100%); 
  }

  .hero-content { 
    position: relative; 
    z-index: 10; 
    width: 100%; 
    max-width: 800px; 
    text-align: center; /* Mobile: Centered */
  }

  .hero-title { font-family: 'Bebas Neue'; font-size: clamp(3rem, 12vw, 8rem); line-height: 0.9; color: white; margin: 1.5rem 0; }
  .text-stroke { -webkit-text-stroke: 1.5px #fff; color: transparent; }
  .hero-sub { color: rgba(255,255,255,0.8); max-width: 550px; font-size: clamp(1rem, 2vw, 1.2rem); margin-bottom: 2.5rem; line-height: 1.6; margin-left: auto; margin-right: auto; }
  
  .hero-badge { 
    background: rgba(255,255,255,0.1); 
    border: 1px solid rgba(255,255,255,0.2); 
    padding: 0.6rem 1.2rem; 
    color: white; 
    display: inline-flex; 
    align-items: center; 
    gap: 10px; 
    font-size: 0.75rem; 
    text-transform: uppercase; 
    letter-spacing: 2px; 
    border-radius: 50px; 
  }
  .pulse-dot { width: 8px; height: 8px; background: var(--red); border-radius: 50%; box-shadow: 0 0 10px var(--red); animation: pulse 2s infinite; }
  @keyframes pulse { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(2.5); opacity: 0; } }

  .hero-btn-group { display: flex; flex-wrap: wrap; gap: 1rem; justify-content: center; }
  .btn-primary { background: var(--red); color: white; border: none; padding: 1.2rem 2.8rem; font-weight: 800; cursor: pointer; display: flex; align-items: center; gap: 10px; transition: var(--transition); }
  .btn-primary:hover { background: #b01f14; transform: translateY(-2px); }
  .btn-outline { background: transparent; color: white; border: 1px solid rgba(255,255,255,0.4); padding: 1.2rem 2.8rem; font-weight: 800; cursor: pointer; transition: var(--transition); }
  .btn-outline:hover { background: white; color: black; }

  /* Desktop Right Alignment Override */
  @media (min-width: 1025px) {
    .hero { justify-content: flex-end; padding-right: 8%; }
    .hero-content { text-align: right; }
    .hero-sub { margin-right: 0; }
    .hero-btn-group { justify-content: flex-end; }
    .hero-badge { margin-left: auto; }
  }

  /* Stats */
  .stats-band { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); background: var(--red); color: white; }
  .stat-item { padding: clamp(2rem, 5vw, 4rem) 1rem; text-align: center; border-right: 1px solid rgba(255,255,255,0.1); }
  .stat-item:last-child { border-right: none; }
  .stat-num { font-family: 'Bebas Neue'; font-size: clamp(3rem, 6vw, 5rem); display: block; line-height: 1; }
  .stat-label { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 2px; opacity: 0.9; }

  /* SLIDER SECTION */
  .video-section { overflow: hidden; }
  .grid-video { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(2rem, 8vw, 6rem); align-items: center; }
  .slider-controls { display: flex; align-items: center; gap: 1.5rem; margin-top: clamp(2rem, 4vw, 4rem); }
  .nav-btn { background: #1a1a1a; border: 1px solid #333; color: white; width: 56px; height: 56px; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: 0.3s; cursor: pointer; }
  .nav-btn:hover { background: var(--red); border-color: var(--red); transform: scale(1.1); }
  .slide-counter { font-family: 'Bebas Neue'; font-size: 1.8rem; letter-spacing: 2px; color: #fff; }

  .youtube-slider-container { position: relative; width: 100%; display: flex; justify-content: center; }
  .phone-frame {
    position: relative;
    width: 100%;
    max-width: 300px;
    aspect-ratio: 9 / 19.5;
    background: #000;
    border: 12px solid #1a1a1a;
    border-radius: 45px;
    overflow: hidden;
    box-shadow: 0 40px 80px rgba(0,0,0,0.6);
    z-index: 2;
  }
  .iframe-wrapper { width: 100%; height: 100%; background: #000; }
  .iframe-wrapper iframe { width: 100%; height: 100%; border: none; }
  .video-glow { position: absolute; width: 140%; height: 100%; background: radial-gradient(circle, var(--red) 0%, transparent 70%); opacity: 0.2; filter: blur(60px); z-index: 1; top: 0; }

  /* Sections */
  .section { padding: clamp(4rem, 12vh, 10rem) 0; }
  .page-wrapper { padding-bottom: clamp(1rem, 2vh, 1.5rem); }
  .bg-dark { background: var(--black); color: white; }
  .section-title { font-family: 'Bebas Neue'; font-size: clamp(2.5rem, 8vw, 5.5rem); line-height: 0.9; margin-bottom: 2rem; }
  .overline { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 4px; font-weight: 800; color: var(--red); margin-bottom: 0.8rem; display: block; }
  .body-text { font-size: clamp(1rem, 1.5vw, 1.15rem); line-height: 1.8; color: inherit; opacity: 0.8; }

  /* Story Grid */
  .grid-story { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(2rem, 6vw, 5rem); align-items: center; }
  .story-image-wrapper { position: relative; width: 100%; }
  .floating-badge { position: absolute; bottom: 20px; left: -20px; background: var(--red); color: white; padding: 1rem 1.5rem; border-radius: 8px; display: flex; align-items: center; gap: 2px; font-weight: 800; font-size: 0.9rem; box-shadow: 0 15px 30px rgba(208, 40, 26, 0.3); }

  .trust-box { display: flex; align-items: center; gap: 1.5rem; padding: 1.2rem; background: white; border-radius: 12px; width: fit-content; margin-top: 2.5rem; box-shadow: 0 20px 40px rgba(0,0,0,0.08); color: #000; }
  .avatar-stack { display: flex; }
  .avatar-stack img { width: 40px; height: 40px; border-radius: 50%; border: 3px solid white; margin-left: -12px; }
  .avatar-stack img:first-child { margin-left: 0; }

  /* Features Grid */
  .grid-features { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1.5rem; margin-top: 3rem; }
  .feature-card { padding: clamp(2rem, 4vw, 3.5rem) clamp(1.5rem, 3vw, 2.5rem); background: #111; border-radius: 20px; border: 1px solid #222; transition: var(--transition); }
  .feature-card:hover { transform: translateY(-10px); background: #161616; border-color: var(--red); }
  .feature-icon { color: var(--red); margin-bottom: 2rem; }
  .feature-card h3 { font-family: 'Bebas Neue'; font-size: 1.8rem; margin-bottom: 1rem; letter-spacing: 1px; }
  .feature-card p { font-size: 1rem; opacity: 0.6; line-height: 1.6; }

  @media (min-width: 1280px) {
    .grid-features { grid-template-columns: repeat(5, 1fr); }
  }

  @media (max-width: 1024px) {
    .grid-video, .grid-story { grid-template-columns: 1fr; text-align: center; }
    .hero-content { margin: 0 auto; }
    .slider-controls { justify-content: center; }
    .trust-box { margin-left: auto; margin-right: auto; }
    .floating-badge { left: 50%; transform: translateX(-50%); bottom: -20px; }
    .video-text { order: 1; }
    .youtube-slider-container { order: 2; }
  }

  @media (max-width: 768px) {
    .stat-item { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.1); }
    .stat-item:last-child { border-bottom: none; }
  }

  @media (max-width: 480px) {
    .phone-frame { max-width: 260px; }
    .hero-btn-group button { width: 100%; justify-content: center; }
  }
`;

export default AboutUs;